import { memo, useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { motion, useAnimation, useInView, type Variants } from 'motion/react'
import { vw, mq, getTransition, getP24_3, imgVariants } from '@/styles'
import { Img } from './commons'
import { ConditionalWrapper } from '@/components'
import type { TypeCmsContentPage } from '@/schema'
import { downloadPDF } from '@/utils'
import { useTranslator } from '@/hooks'

type CardType = {
  data: TypeCmsContentPage['downloads'][0],
  idx: string ,
}

export const CardDownload = memo(({ data, idx }:CardType) => {
  const { name, medium, url, filename, download_type } = data
  const ref = useRef(null)
  const controls = useAnimation()
  const isInView = useInView(ref, { margin:'0px', once: true })
  const [loading, setLoading] = useState(false)
  const { t } = useTranslator()

  useEffect(() => {
    if(isInView) {
      controls.start('animate')
    }
  }, [controls, isInView])


  const downloadPdfOnClick = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(url, {
        method: 'GET',
      })
      const data = await res.blob()
      await downloadPDF(data, `${filename ?? 'nexia_file'}`, setLoading)

    } catch (e) {
      console.warn('downloadPDF - Catch e', e)
      setLoading(false)
    }


  }, [filename, url])

  return (
    <Li ref={ref} key={idx} >
      <motion.article variants={variants} initial='on_exit' animate='on_load' exit='on_exit'>
        <ConditionalWrapper condition={download_type === 'link'} wrapperTrue={children => <a target='_blank' href={url}>{children}</a>} wrapperFalse={children => <button type='button' onClick={downloadPdfOnClick}>{children}</button>}>
          <>
            <Wrapper>
              <motion.div ref={ref} variants={imgVariants} animate={controls} initial={'initial'}>
                <Img media={medium.image} />
              </motion.div>
            </Wrapper>
            <h3>{loading ? t('status.downloading') : name}</h3>
          </>
        </ConditionalWrapper>

      </motion.article>
    </Li>
  )
})



const Li = styled(motion.li)`
  margin-bottom: ${vw(20, 'mobile')};

  ${mq.greaterThan('tablet')}{
    margin-bottom: ${vw(40, 'desktop')};
  }

  article {
    height: ${vw(230, 'mobile')};

    ${mq.greaterThan('tablet')}{
      height: ${vw(426, 'desktop')};
    }
  }

  figure {
    height: ${vw(214, 'mobile')};
    overflow: hidden;
    position: relative;
    width: 100%;

    ${mq.greaterThan('tablet')}{
      height: ${vw(396, 'desktop')};
    }

    img {
      height: 100%;
      object-fit: cover;
      transition: 1000ms transform var(--transition-ease);
      width: 100%;
    }

    &:hover {
      img {
        transform: scale(1.1);
      }
    }
  }

  .placeholder {
    height: ${vw(214, 'mobile')};

    ${mq.greaterThan('tablet')}{
      height: ${vw(396, 'desktop')};
    }
  }

  h3 {
    ${getP24_3()}
    color: var(--color-black);
    text-align: center;
    text-transform: lowercase;
    width: 100%;

    &:first-letter {
      text-transform: uppercase;
    }
  }

  button {
    display: inline-block;
    width: 100%;
  }
`

const Wrapper = styled.div`
  background-color: #EFEFEF;
  height: fit-content;
  margin-bottom: ${vw(5, 'mobile')};
  overflow: hidden;

  ${mq.greaterThan('tablet')}{
    margin-bottom: ${vw(10, 'desktop')};
  }
`

const transition = {
	...getTransition(),
	duration: .5,
} as const

const variants:Variants = {
  on_load: {
    opacity: 1,
    transition
  },
  on_exit: {
    opacity: 0,
    transition
  },
}